import type { Locale } from '@change-corgi/config/locales';

import type { GeoScope, PlaceWithPrediction } from '../shared/types';

type GooglePlaceHookProps = Readonly<{
	input: string;
	geoScope?: GeoScope;
	locale: Locale;
	sessionToken?: google.maps.places.AutocompleteSessionToken;
}>;

const LOCAL_TYPES = [
	'administrative_area_level_1',
	'administrative_area_level_2',
	'locality',
	'neighborhood',
	'sublocality',
];

const getAutocompleteOptions = (geoScope?: GeoScope) => {
	switch (geoScope) {
		case 'street':
			return { includedPrimaryTypes: ['street_address'] };
		case 'local':
			return { includedPrimaryTypes: LOCAL_TYPES };
		case 'national':
			return { includedPrimaryTypes: ['country'] };
		case 'global':
		default:
			return {};
	}
};

export async function getPlacePredictions(
	{ input, geoScope, locale, sessionToken }: GooglePlaceHookProps,
	autocompleteService?: typeof google.maps.places.AutocompleteSuggestion,
	inputLengthApiCallThreshold?: number,
): Promise<PlaceWithPrediction[]> {
	if (!input || (inputLengthApiCallThreshold && input.length < inputLengthApiCallThreshold)) return [];
	if (!sessionToken) return [];

	const autocompleteOptions = getAutocompleteOptions(geoScope);

	const results = await autocompleteService?.fetchAutocompleteSuggestions({
		...autocompleteOptions,
		input,
		language: locale,
		sessionToken,
	});

	return (
		results?.suggestions
			.filter(
				(suggestion): suggestion is { placePrediction: NonNullable<typeof suggestion.placePrediction> } =>
					!!suggestion.placePrediction,
			)
			.map(({ placePrediction }) => ({
				displayName: placePrediction.text.text,
				googlePlacesId: placePrediction.placeId,
				placePrediction,
			})) || []
	);
}
